(() => {
  "use strict";

  const SECTION_SELECTOR =
    "[data-digi-club-how-it-works]";

  const STEP_SELECTOR =
    "[data-digi-club-how-it-works-step]";

  const REVEAL_SELECTOR =
    "[data-digi-club-how-it-works-reveal]";

  const instances = new WeakMap();

  class DigiClubHowItWorks {
    constructor(section) {
      this.section = section;

      this.stepsContainer =
        this.section.querySelector(
          "[data-digi-club-how-it-works-steps]"
        );

      this.steps = Array.from(
        this.section.querySelectorAll(
          STEP_SELECTOR
        )
      );

      this.revealElements = Array.from(
        this.section.querySelectorAll(
          REVEAL_SELECTOR
        )
      );

      this.revealObserver = null;

      this.handlePointerEnter =
        this.handlePointerEnter.bind(this);

      this.handlePointerLeave =
        this.handlePointerLeave.bind(this);

      this.handleFocusIn =
        this.handleFocusIn.bind(this);

      this.handleFocusOut =
        this.handleFocusOut.bind(this);

      this.init();
    }

    init() {
      this.updateStepCount();
      this.bindEvents();
      this.setupReveal();
    }

    updateStepCount() {
      if (!this.stepsContainer) {
        return;
      }

      this.stepsContainer.style.setProperty(
        "--digi-club-how-it-works-step-count",
        String(this.steps.length)
      );
    }

    bindEvents() {
      this.steps.forEach((step) => {
        step.addEventListener(
          "pointerenter",
          this.handlePointerEnter
        );

        step.addEventListener(
          "pointerleave",
          this.handlePointerLeave
        );

        step.addEventListener(
          "focusin",
          this.handleFocusIn
        );

        step.addEventListener(
          "focusout",
          this.handleFocusOut
        );
      });
    }

    unbindEvents() {
      this.steps.forEach((step) => {
        step.removeEventListener(
          "pointerenter",
          this.handlePointerEnter
        );

        step.removeEventListener(
          "pointerleave",
          this.handlePointerLeave
        );

        step.removeEventListener(
          "focusin",
          this.handleFocusIn
        );

        step.removeEventListener(
          "focusout",
          this.handleFocusOut
        );
      });
    }

    handlePointerEnter(event) {
      const step = event.currentTarget;

      this.setActiveStep(step);
    }

    handlePointerLeave(event) {
      const step = event.currentTarget;

      step.classList.remove(
        "is-active"
      );
    }

    handleFocusIn(event) {
      const step = event.currentTarget;

      this.setActiveStep(step);
    }

    handleFocusOut(event) {
      const step = event.currentTarget;

      if (
        step.contains(
          event.relatedTarget
        )
      ) {
        return;
      }

      step.classList.remove(
        "is-active"
      );
    }

    setActiveStep(activeStep) {
      this.steps.forEach((step) => {
        step.classList.toggle(
          "is-active",
          step === activeStep
        );
      });
    }

    selectBlock(blockId) {
      if (!blockId) {
        return;
      }

      const selectedStep =
        this.steps.find(
          (step) =>
            String(
              step.dataset.blockId
            ) ===
            String(blockId)
        );

      if (!selectedStep) {
        return;
      }

      this.steps.forEach((step) => {
        step.classList.toggle(
          "is-selected",
          step === selectedStep
        );
      });

      selectedStep.classList.add(
        "is-visible"
      );

      this.scrollBlockIntoView(
        selectedStep
      );
    }

    deselectBlock(blockId) {
      if (!blockId) {
        return;
      }

      const selectedStep =
        this.steps.find(
          (step) =>
            String(
              step.dataset.blockId
            ) ===
            String(blockId)
        );

      selectedStep?.classList.remove(
        "is-selected"
      );
    }

    scrollBlockIntoView(step) {
      if (!step) {
        return;
      }

      const reducedMotion =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      step.scrollIntoView({
        behavior:
          reducedMotion
            ? "auto"
            : "smooth",
        block: "center",
        inline: "nearest"
      });
    }

    setupReveal() {
      const revealTargets = [
        ...this.revealElements,
        ...this.steps
      ];

      if (!revealTargets.length) {
        return;
      }

      const reducedMotion =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      if (
        reducedMotion ||
        !("IntersectionObserver" in window)
      ) {
        this.showAllRevealElements();
        return;
      }

      this.revealObserver =
        new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              const element =
                entry.target;

              if (
                element.matches(
                  STEP_SELECTOR
                )
              ) {
                const stepIndex =
                  Number(
                    element.dataset.stepIndex
                  ) || 1;

                const delay =
                  Math.min(
                    (stepIndex - 1) * 90,
                    360
                  );

                window.setTimeout(() => {
                  if (!element.isConnected) {
                    return;
                  }

                  element.classList.add(
                    "is-visible"
                  );
                }, delay);
              } else {
                element.classList.add(
                  "is-visible"
                );
              }

              observer.unobserve(
                element
              );
            });
          },
          {
            threshold: 0.16,
            rootMargin:
              "0px 0px -8% 0px"
          }
        );

      revealTargets.forEach(
        (element) => {
          this.revealObserver.observe(
            element
          );
        }
      );
    }

    showAllRevealElements() {
      this.revealElements.forEach(
        (element) => {
          element.classList.add(
            "is-visible"
          );
        }
      );

      this.steps.forEach((step) => {
        step.classList.add(
          "is-visible"
        );
      });
    }

    destroy() {
      this.unbindEvents();

      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }

      this.steps.forEach((step) => {
        step.classList.remove(
          "is-active",
          "is-selected"
        );
      });

      this.section = null;
      this.stepsContainer = null;

      this.steps = [];
      this.revealElements = [];
    }
  }

  function initializeSection(section) {
    if (
      !section ||
      instances.has(section)
    ) {
      return;
    }

    const instance =
      new DigiClubHowItWorks(
        section
      );

    instances.set(
      section,
      instance
    );
  }

  function initializeWithin(root) {
    if (!root) {
      return;
    }

    if (
      root.matches?.(
        SECTION_SELECTOR
      )
    ) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach(
        initializeSection
      );
  }

  function unloadWithin(root) {
    if (!root) {
      return;
    }

    const sections = [];

    if (
      root.matches?.(
        SECTION_SELECTOR
      )
    ) {
      sections.push(root);
    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      const instance =
        instances.get(section);

      if (!instance) {
        return;
      }

      instance.destroy();

      instances.delete(section);
    });
  }

  function getSectionFromEvent(event) {
    return (
      event.target?.closest?.(
        SECTION_SELECTOR
      ) || null
    );
  }

  function handleBlockSelect(event) {
    const blockId =
      event.detail?.blockId;

    if (!blockId) {
      return;
    }

    const section =
      getSectionFromEvent(event);

    if (!section) {
      return;
    }

    const instance =
      instances.get(section);

    instance?.selectBlock(
      blockId
    );
  }

  function handleBlockDeselect(event) {
    const blockId =
      event.detail?.blockId;

    if (!blockId) {
      return;
    }

    const section =
      getSectionFromEvent(event);

    if (!section) {
      return;
    }

    const instance =
      instances.get(section);

    instance?.deselectBlock(
      blockId
    );
  }

  function onReady() {
    initializeWithin(document);
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      onReady,
      {
        once: true
      }
    );
  } else {
    onReady();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeWithin(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      unloadWithin(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:block:select",
    handleBlockSelect
  );

  document.addEventListener(
    "shopify:block:deselect",
    handleBlockDeselect
  );
})();